import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getDeviceHardwareEmoji, getDeviceStatus, formatLastSeen } from '../../utils/deviceHelpers';
import {
  Box,
  Text,
  Spinner,
  Alert,
  AlertIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Button,
  Collapse,
  Badge,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';

const AdminConductores = () => {
  const { t } = useTranslation();
  const [conductoresOriginal, setConductoresOriginal] = useState([]);
  const [conductores, setConductores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('habilitado');
  const [sortOrder, setSortOrder] = useState('desc');
  const [expandedConductorId, setExpandedConductorId] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);
  const [dispositivos, setDispositivos] = useState({});
  const [loadingDispositivos, setLoadingDispositivos] = useState({});
  const [oauthIdentities, setOauthIdentities] = useState({});
  const [loadingOauthIdentities, setLoadingOauthIdentities] = useState({});
  const [expandedDispositivoId, setExpandedDispositivoId] = useState(null);
  const navigate = useNavigate();

  // Columnas a mostrar en la tabla de conductores
  const columnsToDisplay = [
    'id',
    'email',
    'nombre',
    'apellidos',
    'credito',
    'telefono',
    'habilitado',
    'matricula_vehiculo',
    'licencia_conducir',
    'cargo',
    'cif',
    'tipo_ecotaxi',
    'sillita_bebe',
    'apto_minusvalidos',
    'idioma',
    'declaracion_responsable',
    'acepto_normativa_over',
  ];

  useEffect(() => {
    const fetchConductores = async () => {
      try {
        const baseURL = process.env.REACT_APP_API_BASE_URL;
        const token = localStorage.getItem('auth-token');
        
        const response = await fetch(`${baseURL}/api/conductores/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            `Error fetching conductores: ${response.status} - ${
              errorData?.detail || response.statusText
            }`
          );
        }
        const data = await response.json();
        setConductoresOriginal(data);
        setConductores(data);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchConductores();
  }, []);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  useEffect(() => {
    if (sortBy) {
      const sortedConductores = [...conductoresOriginal].sort((a, b) => {
        const valueA = a[sortBy];
        const valueB = b[sortBy];

        if (typeof valueA === 'string' && typeof valueB === 'string') {
          return sortOrder === 'asc'
            ? valueA.localeCompare(valueB)
            : valueB.localeCompare(valueA);
        } else if (typeof valueA === 'number' && typeof valueB === 'number') {
          return sortOrder === 'asc' ? valueA - valueB : valueB - valueA;
        } else if (typeof valueA === 'boolean' && typeof valueB === 'boolean') {
          // Ordenar booleanos: false antes que true para 'asc', true antes que false para 'desc'
          return sortOrder === 'asc'
            ? (valueA === valueB ? 0 : (valueA ? 1 : -1))
            : (valueA === valueB ? 0 : (valueA ? -1 : 1));
        } else {
          // Manejar otros tipos de datos (nulos, etc.) convirtiéndolos a string
          const stringA = String(valueA);
          const stringB = String(valueB);
          return sortOrder === 'asc'
            ? stringA.localeCompare(stringB)
            : stringB.localeCompare(stringA);
        }
      });
      setConductores(sortedConductores);
    } else {
      // Si no hay columna para ordenar, volver a los datos originales sin ordenar
      setConductores(conductoresOriginal);
    }
  }, [sortBy, sortOrder, conductoresOriginal]);

  const handleToggleSection = async (conductorId, section) => {
    const isSameConductor = expandedConductorId === conductorId;
    const isSameSection = expandedSection === section;

    if (isSameConductor && isSameSection) {
      setExpandedConductorId(null);
      setExpandedSection(null);
      setExpandedDispositivoId(null);
      return;
    }

    setExpandedConductorId(conductorId);
    setExpandedSection(section);
    setExpandedDispositivoId(null);

    if (section === 'devices' && !dispositivos[conductorId]) {
      await fetchDispositivos(conductorId);
      return;
    }

    if (section === 'oauth' && !oauthIdentities[conductorId]) {
      await fetchOauthIdentities(conductorId);
    }
  };

  const fetchDispositivos = async (conductorId) => {
    setLoadingDispositivos(prev => ({ ...prev, [conductorId]: true }));
    
    try {
      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const token = localStorage.getItem('auth-token');
      const url = `${baseURL}/api/dispositivos/usuario/${conductorId}`;
      
      console.log('Consultando dispositivos para conductor:', conductorId);
      console.log('URL:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);
      
      if (!response.ok) {
        throw new Error(`Error al obtener dispositivos: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Dispositivos encontrados:', data.length);
      console.log('Datos:', data);
      
      // Ordenar dispositivos por last_seen (más reciente primero)
      const dispositivosOrdenados = data.sort((a, b) => {
        const dateA = new Date(a.last_seen || 0);
        const dateB = new Date(b.last_seen || 0);
        return dateB - dateA; // Descendente
      });
      
      setDispositivos(prev => ({ ...prev, [conductorId]: dispositivosOrdenados }));
    } catch (err) {
      console.error('Error fetching dispositivos:', err);
      setDispositivos(prev => ({ ...prev, [conductorId]: [] }));
    } finally {
      setLoadingDispositivos(prev => ({ ...prev, [conductorId]: false }));
    }
  };

  const fetchOauthIdentities = async (conductorId) => {
    setLoadingOauthIdentities(prev => ({ ...prev, [conductorId]: true }));

    try {
      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const token = localStorage.getItem('auth-token');
      const url = `${baseURL}/api/conductores/${conductorId}/oauth-identities`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Error al obtener OAuth identities: ${response.status}`);
      }

      const data = await response.json();
      setOauthIdentities(prev => ({ ...prev, [conductorId]: Array.isArray(data) ? data : [] }));
    } catch (err) {
      console.error('Error fetching OAuth identities:', err);
      setOauthIdentities(prev => ({ ...prev, [conductorId]: [] }));
    } finally {
      setLoadingOauthIdentities(prev => ({ ...prev, [conductorId]: false }));
    }
  };

  const getProviderColorScheme = (provider) => {
    const providerName = String(provider || '').toLowerCase();
    if (providerName === 'google') return 'red';
    if (providerName === 'facebook') return 'facebook';
    if (providerName === 'instagram') return 'pink';
    if (providerName === 'tiktok') return 'purple';
    return 'gray';
  };

  // Función para renderizar el valor de la celda, incluyendo emoticonos para booleanos
  const renderCellValue = (conductor, key) => {
    if (key === 'id') {
      return (
        <Badge colorScheme="blue" fontSize="xs" p={2} wordBreak="break-all" whiteSpace="normal" className="admin-badge admin-badge-info">
          {conductor.id}
        </Badge>
      );
    }
    
    // Renderizar el campo crédito con formato numérico
    if (key === 'credito') {
      const credito = conductor[key] !== null && conductor[key] !== undefined 
        ? parseFloat(conductor[key]).toFixed(2) 
        : '0.00';
      return (
        <Box fontWeight="medium" color={parseFloat(credito) > 0 ? 'green.500' : 'gray.500'}>
          {credito} puntos
        </Box>
      );
    }
    
    const booleanFields = [
      'habilitado',
      'sillita_bebe',
      'apto_minusvalidos',
      'declaracion_responsable',
      'acepto_normativa_over',
    ];
    if (booleanFields.includes(key)) {
      return conductor[key] ? '✅' : '❌'; // Emoticonos para booleanos
    }
    // Para otros campos, mostrar el valor o 'N/A' si es nulo/indefinido
    const value = conductor[key] !== null && conductor[key] !== undefined
      ? String(conductor[key])
      : 'N/A';
    
    // Campos que necesitan ajuste de texto en múltiples líneas
    if (key === 'licencia_conducir' || key === 'matricula_vehiculo' || key === 'tipo_ecotaxi') {
      return (
        <Box wordBreak="break-word" whiteSpace="normal">
          {value}
        </Box>
      );
    }
    
    return value;
  };

  if (loading) {
    return (
      <Box p={4} display="flex" justifyContent="center" alignItems="center" minH="200px" className="admin-loading">
        <Spinner size="xl" />
        <Text ml={4} className="admin-loading-text">{t('admin_conductores_cargando')}</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4}>
        <Alert status="error" className="admin-alert admin-alert-error">
          <AlertIcon />
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={4} borderWidth="1px" borderRadius="lg" maxWidth="100%" width="100%" className="admin-page-container">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4} flexWrap="wrap" gap={2} className="admin-page-header">
        <Text fontSize="xl" fontWeight="bold" className="admin-page-title">
          {t('admin_conductores_titulo')}
        </Text>
        <Button
          colorScheme="teal"
          onClick={() => navigate('/admin/alta-conductor')}
          className="admin-button-success"
        >
          {t('admin_conductores_crear')}
        </Button>
      </Box>

      {conductores.length > 0 ? (
        <Box overflowX="auto" width="100%" className="admin-table-container">
          <Table variant="simple" size="sm" sx={{ tableLayout: 'fixed', width: '100%' }} className="admin-table">
          <Thead>
            <Tr>
              <Th width="92px"></Th>
              <Th width="60px" whiteSpace="normal">{t('admin_conductores_acciones')}</Th>
              {columnsToDisplay.map((key) => (
                <Th
                  key={key}
                  cursor="pointer"
                  onClick={() => handleSort(key)}
                  width={
                    key === 'id' ? '100px' :
                    key === 'email' ? '200px' :
                    key === 'nombre' ? '120px' :
                    key === 'apellidos' ? '120px' :
                    key === 'credito' ? '100px' :
                    key === 'telefono' ? '100px' :
                    key === 'habilitado' ? '70px' :
                    key === 'matricula_vehiculo' ? '120px' :
                    key === 'licencia_conducir' ? '120px' :
                    key === 'cargo' ? '80px' :
                    key === 'cif' ? '100px' :
                    key === 'tipo_ecotaxi' ? '100px' :
                    key === 'sillita_bebe' ? '70px' :
                    key === 'apto_minusvalidos' ? '80px' :
                    key === 'idioma' ? '60px' :
                    key === 'declaracion_responsable' ? '80px' :
                    key === 'acepto_normativa_over' ? '80px' :
                    'auto'
                  }
                  whiteSpace={
                    ['habilitado', 'sillita_bebe', 'apto_minusvalidos', 'declaracion_responsable', 'acepto_normativa_over', 'licencia_conducir', 'matricula_vehiculo', 'tipo_ecotaxi'].includes(key) ? 'normal' : 'nowrap'
                  }
                >
                  {key === 'credito' ? 'Crédito' : t(`col_${key}`)}
                  {sortBy === key && (sortOrder === 'asc' ? ' ⬆️' : ' ⬇️')}
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {conductores.map((conductor) => (
              <React.Fragment key={conductor.id}>
                <Tr
                  _hover={{ backgroundColor: 'rgba(0, 0, 0, 0.03)' }}
                >
                  <Td>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Box
                        as="button"
                        type="button"
                        onClick={() => handleToggleSection(conductor.id, 'devices')}
                        fontSize="lg"
                        px={2}
                        py={1}
                        borderRadius="md"
                        bg={expandedConductorId === conductor.id && expandedSection === 'devices' ? 'blue.600' : 'blue.500'}
                        color="white"
                        _hover={{ bg: 'blue.600' }}
                        title="Dispositivos vinculados"
                      >
                        {expandedConductorId === conductor.id && expandedSection === 'devices' ? '🔼' : '🔽'}
                      </Box>

                      <Box
                        as="button"
                        type="button"
                        onClick={() => handleToggleSection(conductor.id, 'oauth')}
                        fontSize="lg"
                        px={2}
                        py={1}
                        borderRadius="md"
                        bg={expandedConductorId === conductor.id && expandedSection === 'oauth' ? 'purple.600' : 'purple.500'}
                        color="white"
                        _hover={{ bg: 'purple.600' }}
                        title="OAuth vinculados"
                      >
                        {expandedConductorId === conductor.id && expandedSection === 'oauth' ? '🔼' : '🔽'}
                      </Box>
                    </Box>
                  </Td>
                  <Td>
                    <IconButton
                      aria-label={t('admin_conductores_editar', { nombre: conductor.nombre, apellidos: conductor.apellidos })}
                      icon={<span aria-hidden="true">✏️</span>}
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/admin/editar-conductor/${conductor.id}`);
                      }}
                    />
                  </Td>
                  {columnsToDisplay.map((key) => (
                    <Td key={`${conductor.id}-${key}`}>
                      {renderCellValue(conductor, key)}
                    </Td>
                  ))}
                </Tr>
                <Tr>
                  <Td colSpan={columnsToDisplay.length + 2} p={0} border="none" maxWidth="100vw">
                    <Collapse in={expandedConductorId === conductor.id} animateOpacity>
                      <Box p={4} bg="gray.800" borderRadius="md" m={2} maxWidth="calc(100vw - 40px)" overflow="hidden">
                        {expandedSection === 'devices' && loadingDispositivos[conductor.id] ? (
                          <Box display="flex" alignItems="center">
                            <Spinner size="sm" mr={2} />
                            <Text>Cargando dispositivos...</Text>
                          </Box>
                        ) : expandedSection === 'devices' && dispositivos[conductor.id] && dispositivos[conductor.id].length > 0 ? (
                          <Box>
                            {dispositivos[conductor.id].map((dispositivo) => (
                              <Box key={dispositivo.id} mb={2}>
                                <Box
                                  p={3}
                                  bg="white"
                                  borderRadius="md"
                                  borderWidth="1px"
                                  borderColor="gray.200"
                                  cursor="pointer"
                                  _hover={{ bg: 'white', borderColor: 'gray.400' }}
                                  onClick={() => setExpandedDispositivoId(
                                    expandedDispositivoId === dispositivo.id ? null : dispositivo.id
                                  )}
                                >
                                  <Box display="flex" alignItems="center" gap={2}>
                                    <Text fontSize="2xl">{getDeviceHardwareEmoji(dispositivo.platform, dispositivo.user_agent)}</Text>
                                    <Text fontSize="lg">{getDeviceStatus(dispositivo.last_seen).emoji}</Text>
                                    <Badge colorScheme="purple" fontSize="sm" className="admin-badge admin-badge-info">
                                      {dispositivo.custom_device_id || dispositivo.id.substring(0, 8) || 'Desconocido'}
                                    </Badge>
                                    <Text fontSize="xs" color="gray.500" ml={1}>
                                      ({formatLastSeen(dispositivo.last_seen)})
                                    </Text>
                                    <Badge colorScheme="green" ml="auto" mr={2} className="admin-badge admin-badge-success">
                                      {dispositivo.access_count} accesos
                                    </Badge>
                                    <Text fontSize="xl">
                                      {expandedDispositivoId === dispositivo.id ? '🔼' : '🔽'}
                                    </Text>
                                  </Box>
                                </Box>

                                <Collapse in={expandedDispositivoId === dispositivo.id} animateOpacity>
                                  <Box 
                                    mt={2} 
                                    p={0}
                                    borderRadius="md" 
                                    overflow="hidden"
                                  >
                                    <Box
                                      dangerouslySetInnerHTML={{ __html: dispositivo.html_detalle || '<p>No hay detalles disponibles</p>' }}
                                      sx={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(2, 1fr)',
                                        gap: '20px',
                                        backgroundColor: 'transparent',
                                        '& > div': {
                                          display: 'contents'
                                        },
                                        '& > div > div': {
                                          gridColumn: 'auto'
                                        },
                                        '& .info-box': {
                                          margin: 0,
                                          borderRadius: 0
                                        },
                                        '& table': {
                                          width: '100%',
                                          borderCollapse: 'collapse'
                                        },
                                        '& td': {
                                          padding: '8px',
                                          fontSize: '14px'
                                        },
                                        '@media (max-width: 768px)': {
                                          gridTemplateColumns: '1fr'
                                        }
                                      }}
                                    />
                                  </Box>
                                </Collapse>
                              </Box>
                            ))}
                          </Box>
                        ) : expandedSection === 'devices' ? (
                          <Text color="gray.400">No hay dispositivos registrados para este conductor</Text>
                        ) : expandedSection === 'oauth' && loadingOauthIdentities[conductor.id] ? (
                          <Box display="flex" alignItems="center">
                            <Spinner size="sm" mr={2} />
                            <Text>Cargando OAuth vinculados...</Text>
                          </Box>
                        ) : expandedSection === 'oauth' && oauthIdentities[conductor.id] && oauthIdentities[conductor.id].length > 0 ? (
                          <Box>
                            {oauthIdentities[conductor.id].map((identity) => (
                              <Box
                                key={identity.id}
                                p={3}
                                mb={2}
                                bg="white"
                                borderRadius="md"
                                borderWidth="1px"
                                borderColor="gray.200"
                              >
                                <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                                  <Badge colorScheme={getProviderColorScheme(identity.provider)} className="admin-badge admin-badge-info">
                                    {(identity.provider || 'unknown').toUpperCase()}
                                  </Badge>
                                  <Text fontSize="sm" color="gray.700">
                                    open_id: {identity.open_id || 'N/A'}
                                  </Text>
                                  <Text fontSize="sm" color="gray.700">
                                    union_id: {identity.union_id || 'N/A'}
                                  </Text>
                                </Box>
                                <Box mt={2} display="flex" flexDirection="column" gap={1}>
                                  <Text fontSize="sm" color="gray.700">
                                    email: {identity.email || 'N/A'}
                                  </Text>
                                  <Text fontSize="xs" color="gray.500">
                                    creado: {identity.created_at ? new Date(identity.created_at).toLocaleString() : 'N/A'}
                                  </Text>
                                  <Text fontSize="xs" color="gray.500">
                                    último login: {identity.last_login_at ? new Date(identity.last_login_at).toLocaleString() : 'N/A'}
                                  </Text>
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Text color="gray.400">No hay OAuth vinculados para este conductor</Text>
                        )}
                      </Box>
                    </Collapse>
                  </Td>
                </Tr>
              </React.Fragment>
            ))}
          </Tbody>
        </Table>
        </Box>
      ) : (
        <Text>{t('admin_conductores_sin_registros')}</Text>
      )}
    </Box>
  );
};

export default AdminConductores;